/**
 * Riconciliazione a partire da un **resoconto** (F-105).
 *
 * La pagina gemella di `ProposalTracePage`, letta dall'altro capo: là si parte
 * da un ordine e si risale, qui si parte da un documento e si scende. La
 * visualizzazione è **la stessa** — `TraceFlow` è condiviso, non copiato —
 * perché la richiesta era esattamente quella, e perché due disegni che si
 * somigliano cominciano a divergere alla prima modifica.
 *
 * ## Le due cose che questa pagina deve dire, e che nessun'altra dice
 *
 * 1. **Dove si è interrotta, e perché.** Un resoconto che non ha prodotto
 *    ordini è il caso normale, non un guasto: la spunta non c'era, il
 *    resoconto era vuoto, il motore è spento, nessun candidato ha superato il
 *    cancello. Un elenco vuoto senza spiegazione manderebbe a cercare un
 *    guasto dove non ce n'è, ed è la ragione per cui `stoppedAt` sta in
 *    evidenza e non in fondo.
 * 2. **Quali blocchi della mappa ha attraversato.** Lo stesso diagramma di
 *    `/automations`, con lo stesso schema, ma letto su un caso concreto: i
 *    blocchi toccati si accendono, gli altri si attenuano. Attenuare conta
 *    quanto accendere — l'informazione è anche quali strade la catena *non* ha
 *    preso.
 */

import { Link, useParams } from 'react-router-dom';
import { FlowDiagram } from '@/features/automations/FlowDiagram';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, EmptyState, Spinner, TableWrap } from '@/ui';
import { TraceFlow } from './TraceFlow';
import { STATUS_TONE } from './trace-format';
import { useReportTrace, type ReportTraceKind, type TracedOperation } from './api';
import './auto-trading.css';

const KIND_LABEL: Record<ReportTraceKind, string> = {
  briefing: 'Resoconto di mercato',
  digest: 'Digest di portafoglio',
  analysis: 'Analisi del titolo',
  rotation: 'Rotazione generale',
};

const OPERATION_LABEL: Record<string, string> = {
  executed: 'Eseguita',
  dry_run: 'Simulata',
  rejected: 'Rifiutata',
  deferred: 'Rinviata',
  approved: 'Approvata',
  proposed: 'Proposta',
};

function OperationRow({ operation }: { operation: TracedOperation }) {
  return (
    <tr>
      <td>
        {/* Il collegamento porta alla riconciliazione della SINGOLA
            operazione: è la pagina che esisteva già, e da qui in giù la
            domanda torna a essere «perché questa». */}
        <Link to={operation.href} className="trace__op-link">
          {operation.side === 'buy' ? 'Acquisto' : 'Vendita'} {operation.symbol || operation.instrumentName}
        </Link>
        <span className="cell--minor">{operation.portfolioName}</span>
      </td>
      <td>
        <Badge tone={STATUS_TONE[operation.status] ?? 'neutral'}>
          {OPERATION_LABEL[operation.status] ?? operation.status}
        </Badge>
      </td>
      <td className="cell--numeric">{operation.amount ?? '—'}</td>
      <td>
        {operation.rejectionReasonCode ? (
          <>
            <strong>{operation.rejectionReasonCode}</strong>
            {operation.rejectionDetail && <span className="cell--minor">{operation.rejectionDetail}</span>}
          </>
        ) : (
          <span className="cell--minor">—</span>
        )}
      </td>
      <td className="cell--minor">{operation.runDate}</td>
    </tr>
  );
}

export function ReportTracePage() {
  const { kind, id } = useParams<{ kind: ReportTraceKind; id: string }>();
  const query = useReportTrace(kind, id);

  if (query.isPending) return <Spinner label="Ricostruzione della catena…" />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Alert variant="error" title={notFound ? 'Resoconto non trovato' : 'Errore'}>
        {notFound
          ? 'Questo resoconto non esiste, oppure non è tuo.'
          : query.error instanceof Error
            ? query.error.message
            : 'Errore imprevisto'}
      </Alert>
    );
  }

  const trace = query.data;

  return (
    <div className="trace">
      <header className="page-header">
        <div>
          <h1>{trace.report.title}</h1>
          <p className="page-header__meta">
            {KIND_LABEL[trace.report.kind]}
            {trace.report.subtitle ? ` · ${trace.report.subtitle}` : ''}
            {trace.report.at ? ` · ${formatDateTime(trace.report.at)}` : ''}
          </p>
        </div>
        {trace.report.href && (
          <Link to={trace.report.href} className="btn btn--ghost">
            Apri il resoconto
          </Link>
        )}
      </header>

      {trace.stoppedAt && (
        // In evidenza e non in fondo: è la risposta alla domanda con cui si
        // apre questa pagina, non una nota a margine del suo esito.
        <Alert variant="info" title={`La catena si è fermata: ${trace.stoppedAt.stage}`}>
          {trace.stoppedAt.reason}
        </Alert>
      )}

      <Card
        title="Che cosa ha innescato"
        subtitle="La catena in ordine cronologico. I passi non avvenuti restano visibili: dicono dove si è interrotta."
      >
        <TraceFlow steps={trace.steps} />
      </Card>

      <Card
        title="Operazioni valutate"
        subtitle="Ogni riga porta alla riconciliazione di quella singola operazione."
        flush
      >
        {trace.operations.length === 0 ? (
          <EmptyState
            icon="🛑"
            title="Nessuna operazione"
            description="Questo resoconto non ha prodotto candidati. Il riquadro qui sopra dice a quale passo si è fermato."
          />
        ) : (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th>Operazione</th>
                  <th>Esito</th>
                  <th className="cell--numeric">Importo</th>
                  <th>Motivo del rifiuto</th>
                  <th>Run</th>
                </tr>
              </thead>
              <tbody>
                {trace.operations.map((operation) => (
                  <OperationRow key={operation.proposalId} operation={operation} />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Dove è passata sulla mappa"
        subtitle="Lo stesso schema della pagina Automazioni: i blocchi attraversati da questa catena sono in evidenza, gli altri attenuati."
      >
        <FlowDiagram active={trace.activeBlocks} />
      </Card>
    </div>
  );
}
